/* ATHLO+ — Strava connect UI glue.
   Inert unless the locker rendered #lk-strava-connect (only happens when the theme's
   "Strava connect URL" setting is set AND a customer is logged in). Handles the OAuth
   round-trip return (?strava=connected|denied|error) and reflects the connected state
   on the button. The connect href itself is built in Liquid (member id + locker token),
   so this file just does state + feedback.

   CONSENT (2026-10-01, Strava API Policy 2.3/5.13). A member's Strava data may be shown to
   OTHER people only with their prior express consent, so every connect link now carries
   `public=1|0` and /connect records it before the OAuth redirect. Liquid renders `public=0`
   (the no-JS answer is "no"); the consent box next to each button is the only thing that can
   make it 1 — see stampPublic() at the bottom. Once connected, the Locker's "Show my Strava
   stats publicly" switch changes it through POST /strava/consent. Unlink calls /disconnect
   and READS the answer, because the toast now makes a claim (we deleted your data) that only
   the server can back. */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  // Self-styled floating toast. (We used to borrow the locker's .lk-ach-toast class, but
  // that CSS is scoped to #locker while this element mounts on <body>, so it never applied —
  // the confirmation rendered as an unstyled blob. Inline styles keep it reliable + on-brand.)
  function miniToast(msg, action) {
    try {
      var el = document.createElement('div');
      el.setAttribute('role', 'status');
      el.style.cssText = 'position:fixed;z-index:9999;right:1.5rem;bottom:1.5rem;max-width:360px;' +
        'display:flex;gap:.7rem;align-items:flex-start;padding:.85rem 1rem;' +
        'background:var(--ink-800,#0b0e12);color:var(--fg,#fff);' +
        'border:1px solid var(--lime,#c8ff00);border-radius:var(--radius,12px);' +
        'box-shadow:0 12px 40px rgba(0,0,0,.5);font-size:.85rem;line-height:1.35;' +
        'transform:translateY(calc(100% + 2rem));opacity:0;' +
        'transition:transform .35s cubic-bezier(.34,1.56,.64,1),opacity .35s;';

      var icon = document.createElement('span');
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = '🟠';
      icon.style.cssText = 'font-size:1.4rem;line-height:1;';

      var body = document.createElement('div');
      body.style.cssText = 'display:flex;flex-direction:column;gap:.15rem;';
      var strong = document.createElement('strong');
      strong.textContent = 'Strava';
      strong.style.cssText = 'font-family:var(--font-display,inherit);text-transform:uppercase;letter-spacing:.02em;font-size:.9rem;';
      var span = document.createElement('span');
      span.textContent = msg;
      span.style.cssText = 'color:var(--muted,#8a93a0);';
      body.appendChild(strong);
      body.appendChild(span);

      if (action) {
        var cta = document.createElement('a');
        cta.href = '#';
        cta.textContent = action.label;
        cta.style.cssText = 'display:inline-block;margin-top:.35rem;font-size:.68rem;letter-spacing:.05em;' +
          'text-transform:uppercase;color:var(--lime,#c8ff00);text-decoration:none;cursor:pointer;';
        cta.addEventListener('click', function (e) { e.preventDefault(); try { action.onClick(); } catch (x) {} });
        body.appendChild(cta);
      }

      el.appendChild(icon);
      el.appendChild(body);
      document.body.appendChild(el);
      requestAnimationFrame(function () { el.style.transform = 'translateY(0)'; el.style.opacity = '1'; });
      var ttl = action ? 8000 : 4200;   // leave a retry CTA up long enough to click
      setTimeout(function () {
        el.style.transform = 'translateY(calc(100% + 2rem))';
        el.style.opacity = '0';
        setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 400);
      }, ttl);
    } catch (e) { /* no-op */ }
  }

  // Device-local "I unlinked" override. `strava.connected` is server-authoritative
  // (hydrateOwn overwrites the whole local profile with the server copy on reload),
  // so a plain patchLocal would reappear after a refresh. This flag lives OUTSIDE the
  // profile object so hydrate can't wipe it; it's cleared when they reconnect.
  var UNLINK_KEY = 'hub:strava:unlinked';
  function unlinkedLocally() { try { return localStorage.getItem(UNLINK_KEY) === '1'; } catch (e) { return false; } }

  function stravaState() {
    try { return (window.HubProfile && HubProfile.get().strava) || {}; } catch (e) { return {}; }
  }

  function render() {
    var box = document.getElementById('lk-strava-connect');
    if (!box) return;
    var st = stravaState();
    var connected = !unlinkedLocally() && !!st.connected;
    var lbl = document.getElementById('lk-strava-btn-label');
    var btn = document.getElementById('lk-strava-btn');
    var linked = document.getElementById('lk-strava-linked');
    var consent = document.getElementById('lk-strava-consent');
    var sw = document.getElementById('lk-strava-pubsw');
    if (lbl) lbl.textContent = 'Connect with Strava';
    if (btn) btn.hidden = connected;         // hide the connect CTA once linked
    if (consent) consent.hidden = connected; // ...and the consent box that belongs to it
    if (linked) linked.hidden = !connected;  // show ✓ status + Unlink once linked
    // The switch shows the SERVER's answer (profile.strava.public is written only by the strava
    // function). Never repainted mid-request, or a hydrate landing during the POST would flip it
    // back under the member's finger.
    if (sw && !sw.__busy) sw.checked = connected && st['public'] === true;
    // Hero status tag. render() is already the one place that decides "connected"
    // (it honours the device-local unlink override too), so the tag hangs off it
    // rather than growing a second, subtly-different opinion elsewhere.
    var tags = document.getElementById('lk-tags');
    if (tags) tags.hidden = !connected;
    box.setAttribute('data-connected', connected ? 'true' : 'false');
  }

  // Every other route is addressed off the connect link Liquid rendered, so the function's base
  // URL lives in one place. Built from the path and member_id ONLY: that href also carries the
  // session token and the consent flag by now, and neither belongs on /disconnect, /sync or
  // /consent — the token is re-minted per call and stamped on fresh (withToken, below).
  function stravaRoute(route) {
    var el = document.getElementById('lk-strava-btn');
    var href = (el && el.getAttribute('href')) || '';
    var at = href.indexOf('/strava/connect');
    var m = /[?&]member_id=([^&#]+)/.exec(href);
    if (at === -1 || !m) return '';
    return href.slice(0, at) + '/strava/' + route + '?member_id=' + m[1];
  }
  function stravaMember() {
    var el = document.getElementById('lk-strava-btn');
    var m = /[?&]member_id=([^&#]+)/.exec((el && el.getAttribute('href')) || '');
    try { return m ? decodeURIComponent(m[1]) : ''; } catch (e) { return ''; }
  }
  function sessionToken() {
    var s = window.HubSession;
    return (s && typeof s.token === 'function') ? s.token() : '';
  }
  // GET/POST, then the body AND the status together: an error answer is JSON too, and "the server
  // said no" has to stay distinguishable from "the server said yes" all the way to the toast.
  function askStrava(url, init) {
    return sessionGate().then(function () {
      var tok = sessionToken();
      if (!url || !tok) throw new Error('no session');
      // Returned, not caught here: every caller hands .then() a rejection handler of its own.
      if (init) return fetch(url, init(tok));
      return fetch(withToken(url, tok), { cache: 'no-store' });
    }).then(function (r) {
      return r.json().then(function (d) { return { ok: r.ok, d: d || {} }; });
    });
  }

  // Mirror of the server's purge (purgePbRows / toManualRow in _shared/strava-policy.ts) on this
  // device's cached profile. Without it the Locker goes on showing every Strava PB after an unlink:
  // hydrateOwn() refuses to let a server copy EMPTY a collection held locally, so a member whose only
  // PBs came from Strava would keep them on screen indefinitely. Same rule as the server, and the
  // same WHITELIST: Strava's rows go, hidden tombstones included; a row the member EDITED is their
  // own claim and stays as an ordinary manual row carrying only what a member can type — and its
  // `updated` is reset, because on a Strava row that was the activity's start date. An edited row
  // the member also DISMISSED goes too: kept, it would be invisible here (hidden rows never render)
  // and undeletable, and the next Locker save would write it back to the server as a manual row.
  var MANUAL_KEYS = ['sport', 'event', 'unit', 'value', 'pb', 'is_custom'];
  function stripStravaRows(list, nowIso) {
    var keep = [];
    (Array.isArray(list) ? list : []).forEach(function (r) {
      if (!r || r.source !== 'strava') { keep.push(r); return; }
      if (r.edited !== true || r.hidden === true) return;
      var own = {};
      for (var i = 0; i < MANUAL_KEYS.length; i++) if (r[MANUAL_KEYS[i]] !== undefined) own[MANUAL_KEYS[i]] = r[MANUAL_KEYS[i]];
      own.updated = nowIso;
      keep.push(own);
    });
    return keep;
  }
  function purgeLocalStrava() {
    if (!window.HubProfile || !HubProfile.patchLocal) return;
    var p = HubProfile.get() || {}, now = new Date().toISOString();
    HubProfile.patchLocal({ pbsX: stripStravaRows(p.pbsX, now), strava: { connected: false, 'public': false, updated: now } });
  }

  /* A PURGE MADE ELSEWHERE REACHES THIS DEVICE TOO (review, 2026-10-01). Unlink on another device,
     or a revoke at strava.com that the deauth webhook confirmed, deletes the rows on the server and
     writes strava:{connected:false} — but the hydrate guard above keeps a list this device still
     holds, so every other browser went on showing the deleted PBs, badged and linked to Strava,
     under a toast that had said "we've deleted your Strava data". `connected === false` is written
     by the server's purge and nothing else, and a public read never carries it, so it is the one
     signal that rows here are the deleted ones. patchLocal: no sync, no `updated` bump, so the next
     hydrate still wins. Runs on every page, because the own-profile view paints from this cache. */
  function scrubPurged() {
    var HP = window.HubProfile;
    if (!HP || !HP.get || !HP.patchLocal) return;
    var p = HP.get() || {};
    if (!p.strava || p.strava.connected !== false || !Array.isArray(p.pbsX)) return;
    if (!p.pbsX.some(function (r) { return r && r.source === 'strava'; })) return;
    HP.patchLocal({ pbsX: stripStravaRows(p.pbsX, new Date().toISOString()) });
  }
  ready(function () {
    scrubPurged();
    if (window.HubProfile && HubProfile.on) HubProfile.on(scrubPurged);
  });

  // Unlink Strava — give athletes a clear opt-out. Two-tap confirm, then doUnlink().
  function unlinkStrava() {
    var btn = document.getElementById('lk-strava-unlink');
    if (!btn || btn.disabled) return;
    if (!btn.classList.contains('is-confirm')) {
      btn.classList.add('is-confirm');
      btn.setAttribute('data-label', btn.textContent);
      btn.textContent = 'Tap again to unlink';
      btn._reset = setTimeout(function () {
        btn.classList.remove('is-confirm');
        btn.textContent = btn.getAttribute('data-label') || 'Unlink Strava';
      }, 3500);
      return;
    }
    clearTimeout(btn._reset);
    btn.classList.remove('is-confirm');
    doUnlink();
  }

  /* The unlink used to be a fire-and-forget `mode: 'no-cors'` GET. An opaque response cannot be
     read, so the toast could only guess — and it guessed "your saved PBs stay put", which after the
     2026-10-01 change is the opposite of what happens: API Policy 2.5/7.4 has us DELETE the member's
     Strava data on unlink and confirm it in writing. The toast is that confirmation, so it now says
     only what /disconnect actually answered: `deleted` (the purge ran) and `revoked` (Strava itself
     confirmed our access is gone). Anything short of `deleted` is a failure and changes nothing on
     this device either — a member told "unlinked" whose data is still there has been lied to.

     The session is minted FIRST. The old code sent whatever token was in the connect href, which is
     the one-hour token stamped at page load — on a Locker left open over lunch, a certain 401 that
     no-cors then hid.

     "Nothing was changed" is true only of a refusal. A 500 `purge_incomplete` comes AFTER the purge
     began: the token row may be gone, consent set to private, and Strava may already have confirmed
     the revoke. So that answer says the unlink did not finish and offers the retry — and a revoke
     Strava confirmed on that attempt is remembered, because the retry finds no token row, cannot
     revoke again, answers revoked:false, and would otherwise send the member to strava.com to remove
     an access that is already gone. (In memory only: after a reload the toast errs towards the extra
     step, never towards claiming a revoke nobody confirmed.) */
  var unlinkRevoked = false;
  function doUnlink() {
    var btn = document.getElementById('lk-strava-unlink');
    var label = (btn && btn.getAttribute('data-label')) || 'Unlink Strava';
    function done() { if (btn) { btn.disabled = false; btn.textContent = label; } }
    function fail(partial) {
      done();
      miniToast(partial
        ? 'Unlinking started but didn’t finish — some of your Strava data may still be on ATHLO+. Please try again to finish it.'
        : 'Couldn’t unlink right now — nothing was changed. Please try again.', { label: 'Try again', onClick: doUnlink });
    }
    if (btn) { btn.disabled = true; btn.textContent = 'Unlinking…'; }
    askStrava(stravaRoute('disconnect')).then(function (res) {
      if (res.d.revoked === true) unlinkRevoked = true;
      if (!res.ok || res.d.ok !== true || res.d.deleted !== true) {
        fail(res.d.error === 'purge_incomplete' || res.d.revoked === true);
        return;
      }
      var revoked = res.d.revoked === true || unlinkRevoked;
      unlinkRevoked = false;
      // device-local override (survives a hydrate already in flight) + the local purge
      try { localStorage.setItem(UNLINK_KEY, '1'); } catch (e) { /* no-op */ }
      purgeLocalStrava();
      done();
      render();
      if (revoked) {
        miniToast('Unlinked. We’ve deleted your Strava data from ATHLO+ and revoked our access.');
      } else {
        miniToast('Unlinked. We’ve deleted your Strava data from ATHLO+. To finish, remove ATHLO+ at strava.com/settings/apps.',
          { label: 'Open Strava settings', onClick: function () { window.open('https://www.strava.com/settings/apps', '_blank', 'noopener'); } });
      }
      try { if (window.HubTrack) window.HubTrack('disconnect_strava', { revoked: revoked }); } catch (e) { /* no-op */ }
    }, function () { fail(false); });   // wrapped: the rejection's Error must not read as `partial`
  }

  /* "Show my Strava stats publicly" — the Locker switch. Optimistic: it moves the moment it is
     tapped, and moves BACK if the server does not confirm, with a message that says so. The
     server's answer is what gets mirrored locally, not our request, so the two cannot disagree. */
  function pubMsg(text) {
    var m = document.getElementById('lk-strava-pubmsg');
    if (!m) return;
    m.textContent = text || '';
    m.hidden = !text;
  }
  function setPublic() {
    var sw = document.getElementById('lk-strava-pubsw');
    if (!sw || sw.__busy) return;
    var want = !!sw.checked, was = !want;
    var url = stravaRoute('consent'), member = stravaMember();
    sw.__busy = true; sw.disabled = true;
    pubMsg('Saving…');
    function settle() { sw.__busy = false; sw.disabled = false; }
    function fail() {
      settle();
      sw.checked = was;
      pubMsg('Couldn’t change that right now — nothing was changed. Please try again.');
    }
    askStrava(url && member ? url.split('?')[0] : '', function (tok) {
      var body = { member_id: member, token: tok };
      body['public'] = want;
      return { method: 'POST', cache: 'no-store', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) };
    }).then(function (res) {
      if (!res.ok || res.d.ok !== true || typeof res.d['public'] !== 'boolean') { fail(); return; }
      var now = res.d['public'];
      settle();
      sw.checked = now;
      if (window.HubProfile && HubProfile.patchLocal) {
        var st = {}, cur = stravaState();
        for (var k in cur) if (Object.prototype.hasOwnProperty.call(cur, k)) st[k] = cur[k];
        st['public'] = now;
        HubProfile.patchLocal({ strava: st });
      }
      // "Shown" promises two things the backends can still refuse: neither publishes an under-18
      // member's Strava data, nor anything of a member with "Hide PBs" on (community applyPrivacy,
      // strava-policy boardEligible). Consent is saved either way; the message says what follows.
      var HP = window.HubProfile || {}, p = HP.get ? (HP.get() || {}) : {};
      var minor = !!(HP.u18 && HP.u18(p));
      var hidePbs = !!(p.privacy && p.privacy.metrics);
      pubMsg(!now
        ? 'Hidden. You’re off the leaderboard and your Strava PBs are off your public profile — they stay here in your Locker.'
        : minor
          ? 'Saved — but ATHLO+ keeps the Strava stats of members under 18 off public pages and the leaderboard, so yours stay here in your Locker for now.'
          : hidePbs
            ? 'Saved — but “Hide PBs” is on in your privacy settings, so your Strava stats stay off public pages and the leaderboard until you turn it off.'
            : 'Shown. Your weekly totals join the club leaderboard at its next nightly refresh, and PBs from your public Strava activities show on your profile.');
      try { if (window.HubTrack) window.HubTrack('strava_public', { on: now }); } catch (e) { /* no-op */ }
    }, fail);
  }

  // Remove the strava=… param from a search/hash string and tidy dangling separators.
  function stripStrava(s) {
    return s.replace(/([?&])strava=[a-z]+/, '$1').replace(/[?&]$/, '').replace(/([?&])&/, '$1').replace(/\?$/, '');
  }

  // Re-open the connect button for a one-tap retry (denied/error). The href carries the
  // member id + locker token, so we just navigate to whichever connect CTA is on the page —
  // stamped with what its consent box says NOW, which after the round trip is unticked unless
  // the member ticks it again (the choice is not carried across the OAuth redirect on purpose).
  function retryConnect() {
    var btn = document.getElementById('lk-strava-btn') || document.querySelector('[id^="sc-btn-"]');
    var href = btn && btn.getAttribute('href');
    if (href) window.location.href = stampPublic(btn, href);
  }

  // How many verified (Strava-sourced) PBs are in the profile right now. The connect callback
  // materialises these server-side; they reach the client on the next profile hydrate.
  function stravaPbCount() {
    try {
      var p = (window.HubProfile && HubProfile.get()) || {};
      return (p.pbsX || []).filter(function (r) { return r && r.source === 'strava'; }).length;
    } catch (e) { return 0; }
  }

  // Bridge the gap between "Connected" and the first verified PBs appearing: show a short-lived
  // "Syncing your activities…" banner at the top of the locker that resolves when a Strava PB
  // lands via hydrate — or soft-resolves on a timeout (some athletes have no recent activities).
  function showSyncing() {
    var host = document.getElementById('locker');
    if (!host || document.getElementById('lk-strava-sync')) return;

    if (!document.getElementById('lk-strava-sync-kf')) {
      var st = document.createElement('style');
      st.id = 'lk-strava-sync-kf';
      st.textContent = '@keyframes lkStravaSpin{to{transform:rotate(360deg)}}';
      document.head.appendChild(st);
    }

    var el = document.createElement('div');
    el.id = 'lk-strava-sync';
    el.setAttribute('role', 'status');
    el.style.cssText = 'display:flex;align-items:center;gap:.7rem;max-width:640px;margin:1rem auto 0;padding:.8rem 1rem;' +
      'background:var(--ink-800,#0b0e12);border:1px solid var(--line,rgba(255,255,255,.12));border-left:3px solid #fc4c02;' +
      'border-radius:12px;color:var(--fg,#fff);font-size:.85rem;line-height:1.35;' +
      'opacity:0;transform:translateY(-6px);transition:opacity .3s,transform .3s;';

    var spin = document.createElement('span');
    spin.id = 'lk-strava-sync-mk';
    spin.setAttribute('aria-hidden', 'true');
    spin.style.cssText = 'flex:0 0 auto;width:15px;height:15px;border-radius:50%;border:2px solid rgba(252,76,2,.3);border-top-color:#fc4c02;animation:lkStravaSpin .7s linear infinite;';

    var txt = document.createElement('span');
    txt.id = 'lk-strava-sync-txt';
    txt.textContent = 'Syncing your Strava activities — your verified PBs will appear here shortly.';

    el.appendChild(spin);
    el.appendChild(txt);
    var ob = document.getElementById('lk-onboard');
    if (ob && ob.parentNode) ob.parentNode.insertBefore(el, ob.nextSibling);
    else host.insertBefore(el, host.firstChild);
    requestAnimationFrame(function () { el.style.opacity = '1'; el.style.transform = 'translateY(0)'; });

    var settled = false;
    function finish(kind, msg) {
      if (settled) return; settled = true;
      var mk = document.getElementById('lk-strava-sync-mk');
      var tx = document.getElementById('lk-strava-sync-txt');
      if (mk) {
        mk.style.cssText = 'flex:0 0 auto;font-weight:700;line-height:1;color:' + (kind === 'ok' ? 'var(--lime,#c8ff00)' : '#fc4c02') + ';';
        mk.textContent = kind === 'ok' ? '✓' : '🟠';
      }
      if (tx) tx.textContent = msg;
      el.style.borderLeftColor = kind === 'ok' ? 'var(--lime,#c8ff00)' : '#fc4c02';
      setTimeout(function () {
        el.style.opacity = '0'; el.style.transform = 'translateY(-6px)';
        setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 350);
      }, 6000);
    }

    function check() {
      var n = stravaPbCount();
      if (n > 0) finish('ok', n + (n === 1 ? ' verified PB' : ' verified PBs') + ' synced from Strava. New activities verify automatically.');
    }
    check();                                                          // fast path: PBs already hydrated
    if (window.HubProfile && HubProfile.on) HubProfile.on(check);     // else resolve when they land
    setTimeout(function () {                                          // don't spin forever
      finish('idle', 'Connected — new activities will verify your PBs automatically as you log them.');
    }, 18000);
  }

  /* Kick the backfill. ==========================================================
   * /strava/sync pulls the member's recent activities through the SAME mergePbs
   * the webhook uses. It has existed, auth-gated and covered by
   * _verify/endpoint-auth-test.mjs, since the integration shipped — and nothing
   * has ever called it. The only reference to it in the whole repo was the test
   * asserting it refuses unauthenticated callers.
   *
   * What that cost: /callback stores the token and redirects, so a new member's
   * PB set started EMPTY and filled one activity at a time from the moment they
   * connected. showSyncing() directly above was written expecting this call — it
   * waits for stravaPbCount() to rise above zero and otherwise settles, after 18
   * seconds, to "new activities will verify your PBs as you log them". Which was
   * the only true thing left to say, because nothing was syncing. That is also
   * why the locker looked like it tracked recent form rather than personal bests.
   *
   * Called from the client rather than the callback on purpose: the backfill is
   * one list call plus a detail fetch per activity, and hanging that off the
   * OAuth redirect would put ~11 sequential Strava round trips between the
   * member tapping approve and seeing their locker. Best-effort throughout —
   * showSyncing() settles on its own timer whatever happens here. */
  function runInitialSync() {
    var url = stravaRoute('sync');
    if (!url) return;
    sessionGate().then(function () {
      var s = window.HubSession;
      var tok = (s && typeof s.token === 'function') ? s.token() : '';
      if (tok) url = withToken(url, tok);
      // Owns its rejection: an unhandled one is reported and paged by hub-errlog.
      var req = fetch(url, { cache: 'no-store' });
      req.then(function (r) { return r.ok ? r.json() : null; })
         .then(function (d) {
           // Re-hydrate so the PBs the sync just wrote appear without a reload;
           // HubProfile.on(check) inside showSyncing() then reports the count.
           if (d && d.changed && window.HubProfile && HubProfile.hydrateOwn) HubProfile.hydrateOwn();
         })['catch'](function () { /* best-effort */ });
    }, function () { /* no session, no sync — the webhook still covers new activities */ });
  }

  // On a successful connect, bring the locker into view so the athlete sees the ✓ state in
  // context — the OAuth return lands at the top of the page, but the connected UI lives down
  // in the locker (onboarding card flips Strava → "Connected"). Prefer the always-visible
  // onboarding card; fall back to the locker section.
  function revealLocker() {
    setTimeout(function () {
      var target = document.getElementById('lk-onboard');
      if (!target || target.hidden) target = document.getElementById('locker');
      if (!target) return;
      try {
        if (window.__lenis && typeof window.__lenis.scrollTo === 'function') window.__lenis.scrollTo(target, { offset: -90 });
        else target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (e) { /* no-op */ }
    }, 500);
  }

  function handleReturn() {
    // The OAuth callback returns to `/pages/athlete#locker?strava=…`, so the param lives in
    // the HASH (not the query string) — read both so the connected state reflects immediately
    // instead of waiting for a later hydrate (the "took a few refreshes" bug).
    var m = /[?&]strava=([a-z]+)/.exec(location.search) || /[?&]strava=([a-z]+)/.exec(location.hash);
    if (!m) return;
    var code = m[1];
    var msg = {
      connected: 'Connected — new activities will verify your PBs automatically.',
      denied: 'Connection cancelled.',
      error: 'Connection failed — please try again.',
      // `expired` is new (2026-08-29). The OAuth round trip is the one place a locker session can
      // legitimately die mid-flow — we hand the token to Strava, the member signs in, maybe does
      // 2FA, and comes back. They approved us and did nothing wrong, so this must not read like a
      // fault or send them hunting for a problem that is not there.
      expired: 'Your session timed out while you were on Strava — you approved it fine, just tap again.',
      // 2026-10-15: no more "check back soon" — the waitlist block below the button does the checking.
      full: 'Strava sign-ups are full right now. Tap “Email me when it opens” and we’ll tell you when there’s room.'
    }[code];
    if (msg) {
      var retry = (code === 'denied' || code === 'error' || code === 'expired') ? { label: 'Try again', onClick: retryConnect } : null;
      miniToast(msg, retry);
    }
    if (code === 'connected' && window.HubProfile && HubProfile.patchLocal) {
      // The callback already set this server-side; reflect it locally now so the UI
      // updates without waiting for a hydrate. patchLocal = no sync, no `updated` bump.
      try { localStorage.removeItem(UNLINK_KEY); } catch (e) { /* no-op */ } // reconnected → drop the unlink override
      var p = HubProfile.get();
      p.strava = Object.assign({}, p.strava, { connected: true });
      HubProfile.patchLocal(p);
    }
    // strip strava=… from wherever it lives (search or hash) without a reload, keep #locker
    try {
      history.replaceState(null, '', location.pathname + stripStrava(location.search) + stripStrava(location.hash));
    } catch (e) { /* no-op */ }
    render();
    if (code === 'connected') {
      // XP: Strava connect. Server caps connect_strava at once ever, so the OAuth
      // return can fire it unconditionally without being farmable.
      try { if (window.HubXP && window.HubXP.award) window.HubXP.award('connect_strava'); } catch (e) {}
      try { if (window.HubTrack) window.HubTrack('connect_strava', { method: 'oauth' }); } catch (e) {}
      revealLocker(); showSyncing(); runInitialSync();
    }
  }

  /* THE LOCKER NEEDS THE OWNER'S READ, NOT THE PUBLIC ONE (review, 2026-10-01). hydrateOwn() in
     hub-profile.js does not wait for the session: it sends whatever viewer token exists at that
     instant, and with no token rendered into the page that is usually none, because the mint is an
     App Proxy round trip still in the air. The server then identifies no viewer and answers with the
     PUBLIC copy, which since consent has no strava block at all for a member who has not opted in,
     and only {connected:true} for one who has. Adopted as the owner's profile, that hid Unlink and
     the consent switch from every connected member who had not opted in (after the migration seed,
     all of them) and showed the switch OFF, every PB "Only you", to every member who had.
     The fix belongs in hydrateOwn — gate it on HubSession, as postProfile already is. Until then
     this asks once more, WITH the session, when the first answer may have been the public one:
     it landed before any session existed, or it took away a block this device held from an earlier
     owner read. Once hydrateOwn waits for the session neither can happen, and this costs nothing. */
  function publicShaped(st) {
    var k = (st && typeof st === 'object') ? Object.keys(st) : [];
    return k.length === 0 || (k.length === 1 && k[0] === 'connected');
  }
  function ensureOwnerRead() {
    var HP = window.HubProfile;
    if (!HP || typeof HP.hydrateOwn !== 'function' || typeof HP.hydrated !== 'function' || !HP.on) return;
    var before = stravaState();
    var held = before.connected === true && !publicShaped(before);
    var asked = false;
    function check() {
      if (asked || !HP.hydrated()) return;
      asked = true;
      if (sessionToken() && !(held && publicShaped(stravaState()))) return;   // it was the owner's read
      sessionGate().then(function () { if (sessionToken()) HP.hydrateOwn(); }, function () {});
    }
    check();
    HP.on(check);
  }

  ready(function () {
    if (!document.getElementById('lk-strava-connect')) return;
    ensureOwnerRead();
    render();
    handleReturn();
    var ub = document.getElementById('lk-strava-unlink');
    if (ub) ub.addEventListener('click', unlinkStrava);
    var sw = document.getElementById('lk-strava-pubsw');
    if (sw) sw.addEventListener('change', setPublic);
    if (window.HubProfile && HubProfile.on) HubProfile.on(render);  // re-render after server hydrate
  });

  /* === ADDON (security 2026-08-19): send a SHORT-LIVED token to /strava/connect. ==============
     The connect href is built in Liquid as
         .../strava/connect?member_id={{ customer.id }}&token={{ customer.metafields.custom.locker_token.value }}
     in BOTH sections/hub-locker.liquid and sections/hub-strava-connect.liquid. That token is the
     PERMANENT, non-revocable locker token, and this is the one place it travels in a URL — it goes
     into the query string, and the backend then forwards it inside the OAuth `state` handed to
     Strava. A permanent credential in a URL is a credential in every log, Referer header and
     browser history it passes through, and there is no way to revoke it afterwards.

     hub-session.js already mints a short-lived v2 session token through the App Proxy, and as of
     2026-08-19 the strava function verifies that shape (it was the last of the four backends that
     could not — which is also why LOCKER_LEGACY_TOKENS=0 could not be flipped). So the link is
     rewritten in place once a session token exists.

     ⚠ THE PARAGRAPH BELOW WAS TRUE UNTIL 2026-08-28 AND THEN SILENTLY BECAME FALSE. It is kept,
     struck through, because the way it stopped being true is the whole lesson:

       ~~DEGRADES TO EXACTLY TODAY'S BEHAVIOUR. The swap only happens when HubSession hands back a
       real `v2.` token; if the mint failed, HubSession.token() returns the rendered permanent one
       and this leaves the href untouched, which is what ships now. Nothing here can break the
       button.~~

     Both halves of that rested on one unstated assumption: that Liquid ALWAYS renders `&token=`.
     On 2026-08-28 `locker_token_in_dom` was switched off — correctly, it is a permanent
     non-revocable credential and it had no business in the page — and both connect buttons started
     rendering `?member_id=123` with no token at all. That broke this block in two ways at once:

       1. The rewrite was a SUBSTITUTION, not an insertion: a `v.replace()` whose pattern required
          an existing `token=` matched nothing when there was none, so `next === v` and the href was
          left exactly as it was — tokenless. A successful mint changed nothing.
          (The pattern is not quoted literally here: it ends in the two characters that close a
          block comment, which is its own small lesson about pasting regexes into prose.)
       2. "Degrades to today's behaviour" degraded to a DEAD END. With the permanent token gone from
          the DOM, `HubSession.token()` falls back to the empty string, this returns early, and the
          href is again tokenless.

     Either way the button navigated to `/strava/connect?member_id=…` with no credential, and the
     function answered `401 unauthorized` as `text/plain` — which Safari offers to save as
     `connect.txt`. Every member's Strava connect was broken from the moment the switch was thrown.

     Two comments, each correct in isolation, describing a handoff that did not exist: the Liquid
     said "hub-strava.js swaps in a short-lived token", this said "it degrades to the rendered one".
     Neither was wrong about itself. Nobody checked that the two halves met.

     SO: replace-or-INSERT, and never navigate to a URL we know will 401. The click guard exists
     only for the tokenless case — the old note is right that an anchor awaiting a round trip is a
     worse default, but that trade is only worth making against a *stale* token. Against a
     *guaranteed* 401 it is not a trade at all.
     Retire this block when the permanent token stops being rendered at all. === */
  var CONNECT_SEL = 'a[href*="/strava/connect"], [data-connect*="/strava/connect"]';
  var HAS_TOKEN = /[?&]token=[^&]/;

  function withToken(url, tok) {
    if (HAS_TOKEN.test(url)) return url.replace(/([?&]token=)[^&]*/, '$1' + encodeURIComponent(tok));
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'token=' + encodeURIComponent(tok);
  }

  /* === CONSENT RIDES IN THE SAME URL (2026-10-01). ============================================
     /connect records `public=1|0` BEFORE it redirects to Strava, so whatever the href says at the
     moment of the tap is the member's recorded answer. Liquid renders `public=0`; the consent box
     beside each button (named by the link's data-public-box, looked up by id — no selector work
     in the click path) is the only thing that can turn it to 1.

     The same trap as the token block above, pointed the other way: every rewrite of this href must
     CARRY the flag. A rewrite that rebuilt the URL and dropped it would not break anything visible —
     /connect reads an absent flag as 0 — so a member who ticked the box would silently be recorded
     as private. Hence replace-or-insert again, applied on every path that touches the href: the
     box's change event, upgradeStravaLinks(), the click guard (at the moment of the tap, because a
     browser restoring form state on back/forward sets `checked` WITHOUT firing change), and
     retryConnect(). A link with no box keeps the flag it has, and one with none gets `public=0`. */
  var PUBLIC_RE = /([?&])public=[^&#]*/;
  function withPublic(url, on) {
    var v = on ? '1' : '0';
    if (PUBLIC_RE.test(url)) return url.replace(PUBLIC_RE, '$1public=' + v);
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'public=' + v;
  }
  function publicBox(el) {
    var id = el && el.getAttribute ? el.getAttribute('data-public-box') : '';
    return id ? document.getElementById(id) : null;
  }
  function stampPublic(el, url) {
    var box = publicBox(el);
    if (!box) return PUBLIC_RE.test(url) ? url : withPublic(url, false);
    return withPublic(url, !!box.checked);
  }
  function syncPublicLinks() {
    var nodes = document.querySelectorAll(CONNECT_SEL);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var attr = el.getAttribute('href') ? 'href' : 'data-connect';
      var v = el.getAttribute(attr) || '';
      if (v.indexOf('/strava/connect') === -1) continue;
      var next = stampPublic(el, v);
      if (next !== v) el.setAttribute(attr, next);
    }
  }
  // A member who is ALREADY showing their stats and taps the button again to reconnect must not be
  // flipped to private by a box that merely defaulted to unticked — that would withdraw a consent
  // they never withdrew. So an untouched box mirrors the last answer this device heard from the
  // server once connected, and is unticked for everyone else. It never ticks itself for a member who
  // has not said yes. That cached answer can be stale on a page with no hydrate, which is why the
  // box stays VISIBLE for connected members: what a reconnect records is always on screen.
  function reflectPublicBoxes() {
    var st = {};
    try { st = (window.HubProfile && HubProfile.get().strava) || {}; } catch (e) { st = {}; }
    var on = !unlinkedLocally() && !!st.connected && st['public'] === true;
    var nodes = document.querySelectorAll(CONNECT_SEL);
    for (var i = 0; i < nodes.length; i++) {
      var box = publicBox(nodes[i]);
      if (box && !box.__touched) box.checked = on;
    }
    syncPublicLinks();
  }

  function upgradeStravaLinks() {
    var sess = window.HubSession;
    if (!sess || typeof sess.token !== 'function') return false;
    var tok = sess.token();
    if (!tok) return false;
    var nodes = document.querySelectorAll(CONNECT_SEL), changed = false;
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var attr = el.getAttribute('href') ? 'href' : 'data-connect';
      var v = el.getAttribute(attr) || '';
      if (v.indexOf('/strava/connect') === -1) continue;
      var next = stampPublic(el, withToken(v, tok));
      if (next !== v) { el.setAttribute(attr, next); changed = true; }
    }
    return changed;
  }

  // Is the token we would stamp into the href one we KNOW is dead? Only a minted session token has a
  // visible expiry; a legacy/DOM one has none we can inspect, so the question is only meaningful when
  // the mint is what supplied it. Wrong answers here are costly in both directions: say "stale" too
  // eagerly and every legacy click takes a needless detour, say it too rarely and the member lands on
  // "unauthorized".
  function sessionStale() {
    var s = window.HubSession;
    if (!s || typeof s.source !== 'function' || typeof s.isFresh !== 'function') return false;
    return s.source() === 'session' && !s.isFresh();
  }
  function sessionGate() {
    var s = window.HubSession;
    if (s && typeof s.ensure === 'function') return s.ensure();
    return (s && s.ready) || Promise.resolve();
  }

  ready(function () {
    if (!document.querySelector(CONNECT_SEL)) return;
    // Wire each consent box once (a box can be named by both a link and its data-connect mirror).
    var wired = {}, links = document.querySelectorAll(CONNECT_SEL);
    for (var i = 0; i < links.length; i++) {
      var box = publicBox(links[i]);
      var bid = links[i].getAttribute('data-public-box');
      if (!box || wired[bid]) continue;
      wired[bid] = true;
      box.addEventListener('change', function () { this.__touched = true; syncPublicLinks(); });
    }
    reflectPublicBoxes();
    if (window.HubProfile && HubProfile.on) HubProfile.on(reflectPublicBoxes);
    // Owns its rejection: an unhandled one is reported and paged by hub-errlog.
    sessionGate().then(upgradeStravaLinks, function () {});
    // RE-MINT BEFORE RE-STAMPING. This handler used to call upgradeStravaLinks() synchronously, which
    // reads whatever token is held right now — and the single most likely reason a tab is becoming
    // visible again is that it has been sitting in the background, quite possibly for longer than the
    // one-hour session. It would then stamp the dead token back into every link it had just fixed.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) sessionGate().then(upgradeStravaLinks, function () {});
    });
    window.addEventListener('pageshow', function () { sessionGate().then(upgradeStravaLinks, function () {}); });

    // Last line of defence. If the href carries no token when someone taps it — or carries one we
    // know has expired — the destination is a certain 401, so hold the navigation, give the mint one
    // chance to land, and only then go. If it still cannot produce one, say so rather than showing
    // the member a page that reads "unauthorized". Captures on the anchor's container so it runs
    // before the default.
    document.addEventListener('click', function (ev) {
      var a = ev.target && ev.target.closest && ev.target.closest('a[href*="/strava/connect"]');
      if (!a) return;
      // Consent first, on EVERY click including the modified ones below: the URL must say what
      // the box says at the moment of the tap. Setting href inside the handler is read by the
      // default action that follows, so this needs no preventDefault.
      var was = a.getAttribute('href') || '', stamped = stampPublic(a, was);
      if (stamped !== was) a.setAttribute('href', stamped);
      // Never hijack a modified or non-primary click: cmd/ctrl/shift/middle open in a new tab or
      // window, and preventDefault would silently take that away.
      // Bail only when we POSITIVELY know it is one of those. A synthetic click — from a test
      // harness, or from any code calling el.click() — may carry no `button` at all, and
      // `ev.button !== 0` would treat that absence as a middle-click and skip the guard entirely.
      // Absent means primary here, which is also what a real browser reports.
      if ((ev.button != null && ev.button !== 0) || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
      /* PRESENT IS NOT THE SAME AS VALID, and the first version of this guard conflated them. It
         asked only whether a token was in the href, which is true the whole time a tab sits open —
         including after the session behind that token has expired. The stale case walked straight
         past the guard to the same "unauthorized" screen the guard exists to prevent. */
      if (HAS_TOKEN.test(a.getAttribute('href') || '') && !sessionStale()) return;   // normal path
      ev.preventDefault();
      var fail = function () {
        window.alert('Could not start the Strava connection — your session could not be verified. Please refresh the page and try again.');
      };
      sessionGate().then(function () {
        upgradeStravaLinks();
        var href = a.getAttribute('href') || '';
        if (HAS_TOKEN.test(href) && !sessionStale()) { window.location.href = href; return; }
        fail();
      }, fail);
    }, true);
  });

  /* === ADDON (2026-10-15): STRAVA WAITLIST — "Email me when it opens". =========================
     Strava caps the app at a fixed number of connected athletes until its Developer Program review
     approves more. At the cap the Connect button was a dead end ("check back soon"): a member who
     wanted in left, and nothing could ever tell them there was room. Now, when the strava health
     route answers `full` and this member is not connected, each connect spot on the page — the
     Locker's #lk-strava-connect and every hub-strava-connect section — gets an "Email me when it
     opens" button. It joins strava_waitlist through POST /strava/waitlist (token minted per call by
     askStrava, carried in the body), and the loyalty function's action=strava-waitlist-mail emails
     the oldest waiting members when places open.

     INERT unless BOTH the health route says full:true AND a signed-in connect link (one carrying a
     member_id) is on the page. Not full = nothing on the page changes. Strava's own button artwork
     is never written on: ours sits beneath it. Remove this block and the page is exactly as before. */
  var WAIT_LEAD = 'Strava sign-ups are full right now. Strava limits new apps until it approves more places, and we’ve applied.';
  var WAIT_ERR = 'Couldn’t save that just now — nothing was changed. Please try again.';

  function waitConnected() { return !unlinkedLocally() && !!stravaState().connected; }

  // Every connect spot on the page: where its href lives, and what to put the block after.
  function waitSpots() {
    var out = [];
    var lk = document.getElementById('lk-strava-connect'), lkBtn = document.getElementById('lk-strava-btn');
    if (lk && lkBtn) out.push({ host: lk, href: lkBtn.getAttribute('href') || '', after: lkBtn, lead: WAIT_LEAD });
    // Sections are found by id, not class: #sc-connect-<sid> holds data-connect, and its parts are
    // #sc-cap-<sid> / #sc-btn-<sid>. (Attribute + id lookups are also all the test DOM shims speak.)
    var secs = document.querySelectorAll('[data-connect*="/strava/connect"]');
    for (var i = 0; i < secs.length; i++) {
      var sid = String(secs[i].id || '');
      if (sid.indexOf('sc-connect-') !== 0) continue;
      sid = sid.slice('sc-connect-'.length);
      // The section states the reason itself (#sc-cap-*, the editor's at-capacity note), so no lead here.
      var after = document.getElementById('sc-cap-' + sid) || document.getElementById('sc-btn-' + sid);
      if (after && after.parentNode) out.push({ host: secs[i], href: secs[i].getAttribute('data-connect') || '', after: after, lead: '' });
    }
    return out;
  }
  function waitBase(href) { var at = href.indexOf('/strava/connect'); return at === -1 ? '' : href.slice(0, at) + '/strava/'; }
  function waitMember(href) {
    var m = /[?&]member_id=([^&#]+)/.exec(href || '');
    try { return m ? decodeURIComponent(m[1]) : ''; } catch (e) { return ''; }
  }

  function waitCss() {
    if (document.getElementById('hs-wait-css')) return;
    var st = document.createElement('style');
    st.id = 'hs-wait-css';
    st.textContent =
      '.hs-wait{margin:.75rem 0 0;max-width:46ch}' +
      '.hs-wait__lead{margin:0 0 .6rem;color:#f5b76b;font-family:var(--font-body,inherit);font-size:.82rem;line-height:1.4;text-transform:none}' +
      '.hs-wait__lead:empty{display:none}' +
      '.hs-wait__btn{display:inline-flex;align-items:center;justify-content:center;min-height:44px;padding:.6rem 1.15rem;border-radius:999px;' +
        'border:1px solid var(--lime,#b7ff00);background:var(--lime,#b7ff00);color:var(--ink-900,#0b0e12);font-family:var(--font-body,inherit);' +
        'font-weight:700;font-size:.85rem;letter-spacing:.02em;text-transform:none;cursor:pointer;transition:filter .15s}' +
      '@media (hover:hover) and (pointer:fine){.hs-wait__btn:hover{filter:brightness(1.06)}}' +
      '.hs-wait__btn:focus-visible{outline:2px solid var(--lime,#b7ff00);outline-offset:3px}' +
      '.hs-wait__btn.is-on{background:transparent;color:var(--fg,#fff);border-color:var(--line,rgba(255,255,255,.18))}' +
      '.hs-wait__btn[disabled]{opacity:.6;cursor:progress}' +
      '.hs-wait__msg{margin:.5rem 0 0;color:var(--lime,#b7ff00);font-family:var(--font-body,inherit);font-size:.8rem;line-height:1.4;text-transform:none}' +
      '.hs-wait__msg:empty{display:none}' +
      '#lk-strava-connect[data-full="true"] .lk-strava-btn{opacity:.55;cursor:not-allowed;box-shadow:none;transform:none;filter:none}';
    (document.head || document.documentElement).appendChild(st);
  }

  function initWaitlist() {
    var spots = waitSpots();
    if (!spots.length || waitConnected()) return;
    var href = spots[0].href, base = waitBase(href), member = waitMember(href);
    if (!base || !member) return;                                    // signed out: nothing to wait as
    fetch(base, { cache: 'no-store' }).then(function (r) { return r.json(); }).then(function (h) {
      if (!h || h.full !== true || waitConnected()) return;
      waitCss();
      var state = { waiting: false, busy: true, msg: '' }, blocks = [];
      var lk = document.getElementById('lk-strava-connect'), lkBtn = document.getElementById('lk-strava-btn');
      // The Locker's button keeps its href (the onboarding row and the unlink/consent routes read it);
      // it is marked full and its tap is caught below instead.
      if (lk) lk.setAttribute('data-full', 'true');
      if (lkBtn) lkBtn.setAttribute('aria-disabled', 'true');
      for (var i = 0; i < spots.length; i++) {
        var el = document.createElement('div'), lead = document.createElement('p');
        var btn = document.createElement('button'), msg = document.createElement('p');
        el.className = 'hs-wait';
        lead.className = 'hs-wait__lead'; lead.textContent = spots[i].lead;
        btn.className = 'hs-wait__btn'; btn.setAttribute('type', 'button');
        msg.className = 'hs-wait__msg'; msg.setAttribute('role', 'status'); msg.setAttribute('aria-live', 'polite');
        el.appendChild(lead); el.appendChild(btn); el.appendChild(msg);
        spots[i].after.parentNode.insertBefore(el, spots[i].after.nextSibling);
        blocks.push({ el: el, btn: btn, msg: msg });
      }
      function paint() {
        for (var j = 0; j < blocks.length; j++) {
          var b = blocks[j].btn, m = blocks[j].msg;
          b.textContent = state.waiting ? 'Take me off the list' : 'Email me when it opens';
          b.className = 'hs-wait__btn' + (state.waiting ? ' is-on' : '');
          b.disabled = !!state.busy;
          m.textContent = state.msg || (state.waiting ? 'You’re on the list. We’ll email you as soon as there’s room — no need to check back.' : '');
        }
      }
      function teardown() {
        for (var j = 0; j < blocks.length; j++) if (blocks[j].el.parentNode) blocks[j].el.parentNode.removeChild(blocks[j].el);
        blocks = [];
        if (lk) lk.removeAttribute('data-full');
        if (lkBtn) lkBtn.removeAttribute('aria-disabled');
      }
      function onClick() {
        if (state.busy) return;
        var want = !state.waiting;
        state.busy = true; state.msg = want ? 'Adding you…' : 'Taking you off…'; paint();
        askStrava(base + 'waitlist', function (tok) {
          return { method: 'POST', cache: 'no-store', headers: { 'Content-Type': 'application/json' },
                   body: JSON.stringify({ member_id: member, token: tok, join: want }) };
        }).then(function (res) {
          state.busy = false;
          if (!res.ok || res.d.ok !== true) { state.msg = WAIT_ERR; paint(); return; }
          if (res.d.connected) { teardown(); return; }
          state.waiting = res.d.waiting === true;
          state.msg = state.waiting ? 'Done — you’re on the list. We’ll email you as soon as there’s room.' : 'Okay, you’re off the list.';
          paint();
          try { if (window.HubTrack) window.HubTrack('strava_waitlist', { join: want }); } catch (e) { /* no-op */ }
        }, function () { state.busy = false; state.msg = WAIT_ERR; paint(); });
      }
      for (var k = 0; k < blocks.length; k++) blocks[k].btn.addEventListener('click', onClick);
      paint();
      // Already on the list? Ask, so a member who joined yesterday is not offered it again.
      askStrava(base + 'waitlist?member_id=' + encodeURIComponent(member)).then(function (res) {
        state.busy = false;
        if (res.ok && res.d.ok === true) {
          if (res.d.connected) { teardown(); return; }
          state.waiting = res.d.waiting === true;
        }
        paint();
      }, function () { state.busy = false; paint(); });
      // A hydrate that says "connected" ends it: there is nothing left to wait for.
      if (window.HubProfile && HubProfile.on) HubProfile.on(function () { if (blocks.length && waitConnected()) teardown(); });
      // Catch the Locker button's tap BEFORE the connect guard (window capture runs first): at the cap
      // it can only reach /connect's "full" bounce, so point the member at the list instead.
      window.addEventListener('click', function (ev) {
        if (!lk || lk.getAttribute('data-full') !== 'true') return;
        var a = ev.target && ev.target.closest && ev.target.closest('#lk-strava-btn');
        if (!a) return;
        ev.preventDefault(); ev.stopPropagation();
        var b = blocks.length && blocks[0].btn;
        if (b) { try { b.focus({ preventScroll: false }); } catch (e) { b.focus(); } }
      }, true);
    })['catch'](function () { /* health unreachable: leave the page exactly as it was */ });
  }
  ready(initWaitlist);
})();
